import type {
  BoardFloorPlanData,
  FloorPlanItem,
  FloorPlanMeasure,
  FloorPlanOpening,
  FloorPlanWall,
  FloorPlanWallEnd,
} from '../../../types';
import {
  applyWallLength,
  applyWallSlide,
  applyWallVertex,
  wallAxis,
  wallEndPoint,
  withWallEdit,
} from './floorPlanConstraints';
import {
  clampOpeningOnWall,
  itemAabb,
  normalizePlanExtents,
  pointAlongWall,
  projectOnWall,
} from './floorPlanMath';

export type DimPoint =
  | { kind: 'wall-end'; wallId: string; end: FloorPlanWallEnd }
  | { kind: 'opening-jamb'; openingId: string; end: 'start' | 'end' }
  | { kind: 'item-corner'; itemId: string; corner: 'nw' | 'ne' | 'se' | 'sw' }
  | { kind: 'free'; x: number; y: number };

export function dimPointKey(point: DimPoint): string {
  if (point.kind === 'wall-end') return `wall:${point.wallId}:${point.end}`;
  if (point.kind === 'opening-jamb') return `open:${point.openingId}:${point.end}`;
  if (point.kind === 'item-corner') return `item:${point.itemId}:${point.corner}`;
  return `free:${point.x.toFixed(2)}:${point.y.toFixed(2)}`;
}

export function sameDimPoint(a: DimPoint, b: DimPoint): boolean {
  if (a.kind === 'free' && b.kind === 'free') return Math.hypot(a.x - b.x, a.y - b.y) < 0.6;
  return dimPointKey(a) === dimPointKey(b);
}

export function measureLengthIn(measure: Pick<FloorPlanMeasure, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  return Math.hypot(measure.x2 - measure.x1, measure.y2 - measure.y1);
}

export function hitTestMeasure(
  measures: FloorPlanMeasure[] | undefined,
  x: number,
  y: number,
  threshold = 10,
): FloorPlanMeasure | null {
  let best: FloorPlanMeasure | null = null;
  let bestDist = threshold;
  (measures || []).forEach((measure) => {
    const dx = measure.x2 - measure.x1;
    const dy = measure.y2 - measure.y1;
    const len2 = dx * dx + dy * dy;
    const t = len2 < 0.01 ? 0 : Math.max(0, Math.min(1, ((x - measure.x1) * dx + (y - measure.y1) * dy) / len2));
    const dist = Math.hypot(x - (measure.x1 + dx * t), y - (measure.y1 + dy * t));
    if (dist <= bestDist) {
      best = measure;
      bestDist = dist;
    }
  });
  return best;
}

function itemCorner(item: FloorPlanItem, corner: 'nw' | 'ne' | 'se' | 'sw'): { x: number; y: number } {
  const box = itemAabb(item);
  if (corner === 'nw') return { x: box.x, y: box.y };
  if (corner === 'ne') return { x: box.x + box.w, y: box.y };
  if (corner === 'se') return { x: box.x + box.w, y: box.y + box.h };
  return { x: box.x, y: box.y + box.h };
}

export function listDimPoints(data: Pick<BoardFloorPlanData, 'walls' | 'openings' | 'items'>): DimPoint[] {
  const points: DimPoint[] = [];
  data.walls.forEach((wall) => {
    points.push({ kind: 'wall-end', wallId: wall.id, end: 'start' });
    points.push({ kind: 'wall-end', wallId: wall.id, end: 'end' });
  });
  data.openings.forEach((opening) => {
    points.push({ kind: 'opening-jamb', openingId: opening.id, end: 'start' });
    points.push({ kind: 'opening-jamb', openingId: opening.id, end: 'end' });
  });
  data.items.forEach((item) => {
    (['nw', 'ne', 'se', 'sw'] as const).forEach((corner) => {
      points.push({ kind: 'item-corner', itemId: item.id, corner });
    });
  });
  return points;
}

export function resolveDimPoint(
  data: Pick<BoardFloorPlanData, 'walls' | 'openings' | 'items'>,
  point: DimPoint,
): { x: number; y: number } | null {
  if (point.kind === 'wall-end') {
    const wall = data.walls.find((entry) => entry.id === point.wallId);
    return wall ? wallEndPoint(wall, point.end) : null;
  }
  if (point.kind === 'opening-jamb') {
    const opening = data.openings.find((entry) => entry.id === point.openingId);
    const wall = opening && data.walls.find((entry) => entry.id === opening.wallId);
    if (!opening || !wall) return null;
    return pointAlongWall(wall, point.end === 'start' ? opening.offsetIn : opening.offsetIn + opening.widthIn);
  }
  if (point.kind === 'free') return { x: point.x, y: point.y };
  const item = data.items.find((entry) => entry.id === point.itemId);
  return item ? itemCorner(item, point.corner) : null;
}

function dimPointRank(point: DimPoint): number {
  if (point.kind === 'opening-jamb') return 0;
  if (point.kind === 'wall-end') return 1;
  return 2;
}

export function hitTestDimPoint(
  data: Pick<BoardFloorPlanData, 'walls' | 'openings' | 'items'>,
  x: number,
  y: number,
  threshold = 10,
): DimPoint | null {
  let best: DimPoint | null = null;
  let bestDist = threshold;
  listDimPoints(data).forEach((point) => {
    const at = resolveDimPoint(data, point);
    if (!at) return;
    const dist = Math.hypot(x - at.x, y - at.y);
    if (dist > threshold) return;
    if (
      !best ||
      dist < bestDist - 0.4 ||
      (Math.abs(dist - bestDist) <= 0.4 && dimPointRank(point) < dimPointRank(best))
    ) {
      best = point;
      bestDist = dist;
    }
  });
  return best;
}

function hostWallId(
  data: Pick<BoardFloorPlanData, 'walls' | 'openings'>,
  point: DimPoint,
): string | null {
  if (point.kind === 'wall-end') return point.wallId;
  if (point.kind === 'opening-jamb') {
    return data.openings.find((entry) => entry.id === point.openingId)?.wallId || null;
  }
  return null;
}

function jambOffset(opening: FloorPlanOpening, end: 'start' | 'end'): number {
  return end === 'start' ? opening.offsetIn : opening.offsetIn + opening.widthIn;
}

function moveOpeningJamb(
  wall: FloorPlanWall,
  opening: FloorPlanOpening,
  end: 'start' | 'end',
  nextJamb: number,
): FloorPlanOpening {
  const offsetIn = end === 'start' ? nextJamb : nextJamb - opening.widthIn;
  return { ...opening, ...clampOpeningOnWall(wall, { offsetIn, widthIn: opening.widthIn }) };
}

function axisOffset(from: { x: number; y: number }, to: { x: number; y: number }, axis: 'h' | 'v'): number {
  return axis === 'h' ? to.x - from.x : to.y - from.y;
}

export function applyDimDistance(
  data: BoardFloorPlanData,
  anchor: DimPoint,
  driven: DimPoint,
  inches: number,
  grid = 0,
): BoardFloorPlanData | null {
  if (sameDimPoint(anchor, driven) || !(inches > 0)) return null;
  const pa = resolveDimPoint(data, anchor);
  const pb = resolveDimPoint(data, driven);
  if (!pa || !pb) return null;

  if (driven.kind === 'opening-jamb') {
    const opening = data.openings.find((entry) => entry.id === driven.openingId);
    const wall = opening && data.walls.find((entry) => entry.id === opening.wallId);
    if (!opening || !wall) return null;
    const anchorOnWall = projectOnWall(wall, pa.x, pa.y).rawOffset;
    const current = jambOffset(opening, driven.end) - anchorOnWall;
    const sign = Math.sign(current) || 1;
    const next = moveOpeningJamb(wall, opening, driven.end, anchorOnWall + sign * inches);
    return normalizePlanExtents({
      ...data,
      openings: data.openings.map((entry) => (entry.id === opening.id ? next : entry)),
    });
  }

  const anchorWallId = hostWallId(data, anchor);
  const drivenWallId = hostWallId(data, driven);
  if (
    driven.kind === 'wall-end' &&
    anchor.kind === 'wall-end' &&
    driven.wallId === anchor.wallId &&
    driven.end !== anchor.end
  ) {
    return normalizePlanExtents(
      withWallEdit(
        data,
        applyWallLength(data.walls, data.constraints, driven.wallId, inches, anchor.end, grid),
      ),
    );
  }

  if (anchorWallId && drivenWallId && anchorWallId !== drivenWallId) {
    const aWall = data.walls.find((entry) => entry.id === anchorWallId);
    const bWall = data.walls.find((entry) => entry.id === drivenWallId);
    if (aWall && bWall && wallAxis(aWall) === wallAxis(bWall)) {
      const axis = wallAxis(bWall);
      const current = axis === 'h' ? bWall.y1 - aWall.y1 : bWall.x1 - aWall.x1;
      const sign = Math.sign(current) || 1;
      const delta = sign * inches - current;
      return normalizePlanExtents(
        withWallEdit(
          data,
          applyWallSlide(
            data.walls,
            data.constraints,
            bWall.id,
            bWall,
            axis === 'v' ? delta : 0,
            axis === 'h' ? delta : 0,
            grid,
          ),
        ),
      );
    }
  }

  if (driven.kind === 'wall-end') {
    const wall = data.walls.find((entry) => entry.id === driven.wallId);
    if (!wall) return null;
    const axis = wallAxis(wall);
    const current = axisOffset(pa, pb, axis);
    const sign = Math.sign(current) || 1;
    const next = axis === 'h'
      ? { x: pa.x + sign * inches, y: pb.y }
      : { x: pb.x, y: pa.y + sign * inches };
    return normalizePlanExtents(
      withWallEdit(
        data,
        applyWallVertex(data.walls, data.constraints, wall.id, driven.end, next.x, next.y, { snap: false, grid }),
      ),
    );
  }

  if (driven.kind === 'item-corner') {
    const item = data.items.find((entry) => entry.id === driven.itemId);
    if (!item) return null;
    const axis: 'h' | 'v' = Math.abs(pb.x - pa.x) >= Math.abs(pb.y - pa.y) ? 'h' : 'v';
    const current = axisOffset(pa, pb, axis);
    const sign = Math.sign(current) || 1;
    const delta = sign * inches - current;
    return normalizePlanExtents({
      ...data,
      items: data.items.map((entry) =>
        entry.id === item.id
          ? { ...entry, x: entry.x + (axis === 'h' ? delta : 0), y: entry.y + (axis === 'v' ? delta : 0) }
          : entry,
      ),
    });
  }

  return null;
}

export function dimSelection(driven: DimPoint): { kind: 'wall' | 'opening' | 'item'; id: string } | null {
  if (driven.kind === 'wall-end') return { kind: 'wall', id: driven.wallId };
  if (driven.kind === 'opening-jamb') return { kind: 'opening', id: driven.openingId };
  if (driven.kind === 'item-corner') return { kind: 'item', id: driven.itemId };
  return null;
}

export function dimDistance(
  data: Pick<BoardFloorPlanData, 'walls' | 'openings' | 'items'>,
  anchor: DimPoint,
  driven: DimPoint,
): number | null {
  const pa = resolveDimPoint(data, anchor);
  const pb = resolveDimPoint(data, driven);
  if (!pa || !pb) return null;
  const host = driven.kind === 'opening-jamb'
    ? data.walls.find((wall) => wall.id === data.openings.find((o) => o.id === driven.openingId)?.wallId)
    : null;
  if (host) {
    return Math.abs(projectOnWall(host, pb.x, pb.y).rawOffset - projectOnWall(host, pa.x, pa.y).rawOffset);
  }
  if (driven.kind === 'wall-end') {
    const wall = data.walls.find((entry) => entry.id === driven.wallId);
    if (wall) return Math.abs(axisOffset(pa, pb, wallAxis(wall)));
  }
  if (anchor.kind === 'free' || driven.kind === 'free') {
    return Math.hypot(pb.x - pa.x, pb.y - pa.y);
  }
  return Math.max(Math.abs(pb.x - pa.x), Math.abs(pb.y - pa.y));
}
